import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractComponent,
  Channel,
  ChannelService,
  ConfirmationComponent,
  HYBRID_OFFICIAL_SHARES,
  HybridOfficialShare,
  HybridSessionPairing,
} from 'lib-trend-core';
import { filter, Subject, switchMap, takeUntil, takeWhile, timer } from 'rxjs';

/** How often the pairing QR is refreshed while the business app has not scanned it. */
const PAIRING_REFRESH_MS = 30000;

/**
 * Session side and routing share of a Hybrid channel (SPEC-2026-10-whatsapp-hybrid-channel
 * RF-02, RF-03, RF-06, RF-26; AC-02, AC-05, AC-19).
 *
 * The official channel carries the session state in `hybridConfig`; the session document itself is
 * hidden, so this screen only talks to the `/channel/hybrid/:idChannel/*` routes. Pairing, unpairing
 * and the share are restricted to channel managers (admin) — the server enforces it too.
 */
@Component({
    selector: 'channel-hybrid-session-component',
    templateUrl: './channel-hybrid-session.component.html',
    styleUrls: ['./channel-hybrid-session.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChannelHybridSessionComponent extends AbstractComponent implements OnInit, OnChanges {

  /** The Hybrid official (CLOUD_API) channel. */
  @Input() channel: Channel;

  /** Emits the official channel after a change to its session side or share. */
  @Output() channelChange = new EventEmitter<Channel>();

  readonly shares: ReadonlyArray<HybridOfficialShare> = HYBRID_OFFICIAL_SHARES;
  readonly shareControl = new FormControl<HybridOfficialShare>(25);

  /** QR to scan with the WhatsApp Business app; set only while a pairing is in progress. */
  qrCode: string | null = null;
  pairing = false;
  savingShare = false;

  private readonly stopPairing$ = new Subject<void>();

  constructor(
    injector: Injector,
    private readonly channelService: ChannelService,
    private readonly dialog: MatDialog,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.syncShareControl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['channel']) {
      this.syncShareControl();
    }
  }

  override ngOnDestroy(): void {
    this.stopPairing$.next();
    this.stopPairing$.complete();
    super.ngOnDestroy();
  }

  /**
   * The routing cycle of the selected share, in its smallest terms: 25 % is 1 official message in
   * every 4 free-form ones, 75 % is 3 in 4, 100 % is all of them. Each cycle starts official.
   */
  get shareCycle(): { official: number; cycle: number } {
    const share = this.shareControl.value ?? 25;
    const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
    const divisor = gcd(share, 100);
    return { official: share / divisor, cycle: 100 / divisor };
  }

  get canManage(): boolean {
    return this.isAdmin();
  }

  /** A session document exists (paired now, or paired before and dropped). */
  get hasSession(): boolean {
    return !!this.channel?.hybridConfig?.sessionChannel;
  }

  get sessionConnected(): boolean {
    return this.channel?.hybridConfig?.sessionConnected === true;
  }

  get sessionMismatch(): boolean {
    return this.channel?.hybridConfig?.sessionMismatch === true;
  }

  /** The session pairs on top of the coexistence onboarding — the official side must be up first. */
  get officialConnected(): boolean {
    return this.channel?.connected === true;
  }

  /**
   * Starts (or restarts) the pairing: each call creates the session side if absent, re-reads its
   * state and returns a fresh QR. Repeats until the business app scans it or the screen is left.
   */
  pair(): void {
    if (!this.canManage || this.pairing) return;
    this.pairing = true;
    this.qrCode = null;
    timer(0, PAIRING_REFRESH_MS).pipe(
      switchMap(() => this.channelService.pairHybridSession(this.channel._id)),
      takeWhile((result: HybridSessionPairing) => !result.connected, true),
      takeUntil(this.stopPairing$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (result: HybridSessionPairing) => {
        this.qrCode = result.connected ? null : (result.qrCode ?? null);
        if (result.connected) {
          this.alertService.success(this.t('channel.hybridSession.pairedSuccess'));
          this.reloadChannel();
        }
      },
      error: (err) => {
        this.pairing = false;
        this.qrCode = null;
        this.alertService.error(err.error?.message ?? this.t('channel.hybridSession.pairError'));
      },
      complete: () => {
        this.pairing = false;
      },
    });
  }

  cancelPairing(): void {
    this.stopPairing$.next();
    this.pairing = false;
    this.qrCode = null;
    this.reloadChannel();
  }

  /** Unpairs the session side (AC-05): the channel keeps working as official-only. */
  unpair(): void {
    if (!this.canManage) return;
    this.dialog.open(ConfirmationComponent, {
      width: '600px',
      data: this.t('channel.hybridSession.unpairConfirm'),
    }).afterClosed().pipe(
      filter((result) => Boolean(result) === true),
      switchMap(() => {
        this.loading = true;
        return this.channelService.unpairHybridSession(this.channel._id);
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (channel: Channel) => {
        this.loading = false;
        this.alertService.success(this.t('channel.hybridSession.unpairedSuccess'));
        this.emit(channel);
      },
      error: (err) => {
        this.loading = false;
        this.alertService.error(err.error?.message);
      },
    });
  }

  /** Applies a new official share (RF-26) — the attendance cycles keep their position. */
  onShareChange(): void {
    const share = this.shareControl.value;
    if (!this.canManage || share === this.channel?.hybridConfig?.officialShare) return;
    this.savingShare = true;
    this.channelService.updateHybridShare(this.channel._id, share)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel: Channel) => {
          this.savingShare = false;
          this.alertService.success(this.t('channel.hybridSession.shareUpdatedSuccess'));
          this.emit(channel);
        },
        error: (err) => {
          this.savingShare = false;
          this.syncShareControl();
          this.alertService.error(err.error?.message);
        },
      });
  }

  private reloadChannel(): void {
    this.channelService.getById(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (channel: Channel) => this.emit(channel) });
  }

  private emit(channel: Channel): void {
    if (!channel) return;
    this.channel = channel;
    this.syncShareControl();
    this.channelChange.emit(channel);
  }

  private syncShareControl(): void {
    const share = this.channel?.hybridConfig?.officialShare ?? 25;
    this.shareControl.setValue(share, { emitEvent: false });
    if (this.canManage) {
      this.shareControl.enable({ emitEvent: false });
    } else {
      this.shareControl.disable({ emitEvent: false });
    }
  }
}
